import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type='hidden'])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
].join(", ");

// Counted rather than a boolean so overlapping modals do not un-hide the background when the first
// of them closes.
let openModalCount = 0;

const setBackgroundHidden = (isHidden) => {
  const background = document.getElementById("application-container");

  if (!background) {
    return;
  }

  if (isHidden) {
    background.setAttribute("aria-hidden", "true");
  } else {
    background.removeAttribute("aria-hidden");
  }
};

// Modals render into a portal that is a sibling of the application container, so without this both
// Tab and a screen reader's own navigation (VoiceOver's VO+arrows, NVDA's browse mode) walk straight
// out of the dialog into the page behind it. aria-modal alone is not reliably honoured, so the
// background is explicitly hidden from assistive tech for as long as a modal is open.
//
// Keeps focus inside while open and hands it back to whatever opened the dialog on close.
export const useFocusTrap = (isOpen, containerRef) => {
  const previouslyFocusedRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;

    if (!isOpen || !container) {
      return undefined;
    }

    previouslyFocusedRef.current = document.activeElement;
    container.focus();

    openModalCount += 1;
    setBackgroundHidden(true);

    const onKeyDown = (event) => {
      if (event.key !== "Tab") {
        return;
      }

      const focusables = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const firstFocusable = focusables[0];
      const lastFocusable = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (!focusables.includes(active)) {
        // Focus is on the dialog container itself, or has escaped behind the overlay.
        event.preventDefault();
        (event.shiftKey ? lastFocusable : firstFocusable).focus();
      } else if (event.shiftKey && active === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && active === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    return () => {
      document.removeEventListener("keydown", onKeyDown, true);

      openModalCount = Math.max(0, openModalCount - 1);

      if (openModalCount === 0) {
        setBackgroundHidden(false);
      }

      const previouslyFocused = previouslyFocusedRef.current;

      if (previouslyFocused && previouslyFocused.focus && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [isOpen, containerRef]);
};

export default useFocusTrap;
